/**
 * Form to submit a new holiday (leave) request.
 */
import React, { useState, useEffect, useMemo } from 'react';
import hrApi from '../api';
import { publicHolidayDateSet, countWorkingDaysInRange } from '../utils/workingDays';
import DaysTakenBreakdownModal from './DaysTakenBreakdownModal';

const REQUEST_TYPES = [
  { code: 'annual', name: 'Annual leave' },
  { code: 'overtime', name: 'Overtime leave' },
  { code: 'illness', name: 'Illness', instructions: 'Include medical certificate if more than 3 days.' },
  { code: 'compensation-extra-hours', name: 'Compensation for extra hours' },
  { code: 'doctor-appointment', name: 'Doctor appointment' },
  { code: 'birth-adoption', name: 'Birth or adoption' },
  { code: 'house-move', name: 'House move' },
  { code: 'unpaid-leave', name: 'Unpaid leave' },
  { code: 'care-family-member', name: 'Care of family member' },
  { code: 'accident', name: 'Accident' },
  { code: 'military-service', name: 'Military service', instructions: 'Attach copy of marching orders.' },
  { code: 'death-family-member', name: 'Death of family member' },
  { code: 'marriage', name: 'Marriage' },
  { code: 'training', name: 'Training', instructions: 'Specify name of the course.' },
  { code: 'other', name: 'Other', instructions: 'Specify reason.' },
];

function formatDateForInput(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function HolidayRequestForm({ onSubmit, loading }) {
  const now = new Date();
  const [startDate, setStartDate] = useState(formatDateForInput(now));
  const [endDate, setEndDate] = useState(formatDateForInput(now));
  const [halfDay, setHalfDay] = useState(null); // null = full day(s), 'am' = morning off, 'pm' = afternoon off
  const [comment, setComment] = useState('');
  const [leaveTypeCode, setLeaveTypeCode] = useState('annual');
  const [publicHolidays, setPublicHolidays] = useState([]);
  const [employeeWorkingSettings, setEmployeeWorkingSettings] = useState(null);
  const [overrideWindows, setOverrideWindows] = useState([]);
  const [showBreakdownModal, setShowBreakdownModal] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);

  const ACCEPT_ATTACHMENTS = '.pdf,.doc,.docx,image/jpeg,image/png,image/gif,image/webp';
  const MAX_ATTACHMENTS = 5;
  const MAX_FILE_MB = 10;

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    const maxBytes = MAX_FILE_MB * 1024 * 1024;
    const valid = files.filter((f) => f.size <= maxBytes);
    if (valid.length !== files.length) return; // skip oversized files (max 10MB each)
    setSelectedFiles((prev) => {
      const next = [...prev, ...valid].slice(0, MAX_ATTACHMENTS);
      return next;
    });
    e.target.value = '';
  };

  const removeFile = (index) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  useEffect(() => {
    hrApi
      .get('/api/default-working-hours')
      .then((res) => {
        const wd = res.data?.workingDays;
        const workingDays = Array.isArray(wd) && wd.length > 0 ? wd : [1, 2, 3, 4, 5];
        const ds = res.data?.daySchedule;
        const daySchedule = ds && typeof ds === 'object' && !Array.isArray(ds) ? ds : undefined;
        setEmployeeWorkingSettings({ workingDays, daySchedule });
      })
      .catch(() => setEmployeeWorkingSettings(null));
    hrApi
      .get('/api/work-schedule-overrides/mine')
      .then((res) => setOverrideWindows(Array.isArray(res.data) ? res.data : []))
      .catch(() => setOverrideWindows([]));
  }, []);

  const yearsNeeded = useMemo(() => {
    if (!startDate || !endDate) return [];
    const start = new Date(startDate);
    const end = new Date(endDate);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return [];
    const y1 = start.getFullYear();
    const y2 = end.getFullYear();
    const years = [];
    for (let y = Math.min(y1, y2); y <= Math.max(y1, y2); y++) years.push(y);
    return years;
  }, [startDate, endDate]);

  useEffect(() => {
    if (yearsNeeded.length === 0) {
      setPublicHolidays([]);
      return;
    }
    const fetches = yearsNeeded.map((year) =>
      hrApi.get('/api/public-holidays', { params: { year } }).then((res) => ({ year, list: Array.isArray(res.data) ? res.data : [] }))
    );
    Promise.all(fetches).then((results) => {
      const combined = results.flatMap((r) => r.list);
      setPublicHolidays(combined);
    }).catch(() => setPublicHolidays([]));
  }, [yearsNeeded.join(',')]);

  useEffect(() => {
    if (halfDay === 'am' || halfDay === 'pm') setEndDate(startDate);
  }, [halfDay, startDate]);

  const daysTaken = useMemo(() => {
    if (!startDate || !endDate) return null;
    if (halfDay === 'am' || halfDay === 'pm') return 0.5;
    const start = new Date(startDate);
    const end = new Date(endDate);
    if (end < start) return null;
    const holidaySet = publicHolidayDateSet(publicHolidays);
    return countWorkingDaysInRange(start, end, holidaySet, employeeWorkingSettings, overrideWindows);
  }, [startDate, endDate, halfDay, publicHolidays, employeeWorkingSettings, overrideWindows]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!startDate || !endDate) return;
    const start = new Date(startDate);
    const end = new Date(endDate);
    if (end < start) return;
    const isHalf = halfDay === 'am' || halfDay === 'pm';
    if (isHalf && startDate !== endDate) return;
    onSubmit({
      startDate: isHalf ? startDate : startDate,
      endDate: isHalf ? startDate : endDate,
      halfDay: isHalf ? halfDay : undefined,
      comment: comment.trim(),
      leaveTypeCode: leaveTypeCode || 'annual',
      files: selectedFiles.length ? selectedFiles : undefined,
    });
    setSelectedFiles([]);
  };

  const contractHalfDayType = useMemo(() => {
    if (!startDate) return null;
    const d = new Date(startDate + 'T12:00:00');
    if (Number.isNaN(d.getTime())) return null;
    const weekday = d.getDay(); // 0=Sun..6=Sat, schedule keys are '1'..'5'
    const sched = employeeWorkingSettings?.daySchedule && typeof employeeWorkingSettings.daySchedule === 'object'
      ? employeeWorkingSettings.daySchedule[String(weekday)]
      : null;
    return sched === 'morning' || sched === 'afternoon' ? sched : null;
  }, [startDate, employeeWorkingSettings?.daySchedule]);

  const amDisabledByContract = contractHalfDayType === 'afternoon';
  const pmDisabledByContract = contractHalfDayType === 'morning';

  return (
    <form onSubmit={handleSubmit} className="mb-4">
      <div className="row g-2 align-items-end">
        <div className="col-12 col-md-6">
          <label className="form-label small">Duration</label>
          <div className="d-flex flex-wrap gap-3">
            <label className="form-check form-check-inline mb-0">
              <input type="radio" className="form-check-input" name="halfDay" checked={!halfDay} onChange={() => setHalfDay(null)} />
              <span className="form-check-label small">Full day</span>
            </label>
            <label className="form-check form-check-inline mb-0">
              <input
                type="radio"
                className="form-check-input"
                name="halfDay"
                checked={halfDay === 'am'}
                onChange={() => setHalfDay('am')}
                disabled={amDisabledByContract}
                title={amDisabledByContract ? 'Not applicable for contract schedule' : ''}
              />
              <span className="form-check-label small">Half day (AM off)</span>
            </label>
            <label className="form-check form-check-inline mb-0">
              <input
                type="radio"
                className="form-check-input"
                name="halfDay"
                checked={halfDay === 'pm'}
                onChange={() => setHalfDay('pm')}
                disabled={pmDisabledByContract}
                title={pmDisabledByContract ? 'Not applicable for contract schedule' : ''}
              />
              <span className="form-check-label small">Half day (PM off)</span>
            </label>
          </div>
          {(amDisabledByContract || pmDisabledByContract) && (
            <div className="form-text text-muted mt-1">
              Half-day options are limited by the employee’s contract schedule.
            </div>
          )}
        </div>
      </div>
      <div className="row g-2 align-items-end mt-1">
        <div className="col-auto">
          <label className="form-label small">Leave type</label>
          <select
            className="form-select form-select-sm"
            value={leaveTypeCode}
            onChange={(e) => setLeaveTypeCode(e.target.value)}
          >
            {REQUEST_TYPES.map((t) => (
              <option key={t.code} value={t.code}>{t.name}</option>
            ))}
          </select>
          {(() => {
            const selected = REQUEST_TYPES.find((t) => t.code === leaveTypeCode);
            return selected?.instructions ? (
              <div className="form-text text-muted mt-1" style={{ maxWidth: 260 }}>
                <i className="bi bi-info-circle me-1" />
                {selected.instructions}
              </div>
            ) : null;
          })()}
        </div>
        <div className="col-auto">
          <label className="form-label small">From</label>
          <input
            type="date"
            className="form-control form-control-sm"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            required
          />
        </div>
        <div className="col-auto">
          <label className="form-label small">To</label>
          <input
            type="date"
            className="form-control form-control-sm"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            required
            disabled={halfDay === 'am' || halfDay === 'pm'}
            title={halfDay ? 'Same as From for half day' : ''}
          />
        </div>
        <div className="col-auto">
          <label className="form-label small">Days taken</label>
          <div className="d-flex align-items-center gap-1">
            <div className="form-control form-control-sm bg-light text-muted d-inline-block" style={{ minWidth: '4rem' }}>
              {daysTaken != null ? (daysTaken === 0.5 ? '0.5 days' : `${daysTaken} day${daysTaken !== 1 ? 's' : ''}`) : '–'}
            </div>
            {daysTaken != null && (
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm p-1"
                onClick={() => setShowBreakdownModal(true)}
                title="Show breakdown"
                aria-label="Show days breakdown"
              >
                <i className="bi bi-calendar3" />
              </button>
            )}
          </div>
          <DaysTakenBreakdownModal
            show={showBreakdownModal}
            onClose={() => setShowBreakdownModal(false)}
            startDate={startDate}
            endDate={endDate}
            publicHolidays={publicHolidays}
            daysTaken={daysTaken ?? 0}
            employeeWorkingSettings={employeeWorkingSettings}
            overrideWindows={overrideWindows}
          />
        </div>
        <div className="col">
          <label className="form-label small">Reason (optional)</label>
          <input
            type="text"
            className="form-control form-control-sm"
            placeholder="e.g. Family trip"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          />
        </div>
        <div className="col-auto">
          <button type="submit" className="btn btn-primary btn-sm" disabled={loading}>
            {loading ? 'Submitting...' : 'Submit request'}
          </button>
        </div>
      </div>
      <div className="row g-2 mt-1">
        <div className="col-12">
          <label className="form-label small">Attach documents (optional)</label>
          <p className="small text-muted mb-1">PDF, DOC, DOCX or images. Max {MAX_FILE_MB}MB per file, up to {MAX_ATTACHMENTS} files. e.g. medical certificate, military duty document.</p>
          <div className="d-flex flex-wrap align-items-center gap-2">
            <input
              type="file"
              className="form-control form-control-sm"
              style={{ maxWidth: 260 }}
              accept={ACCEPT_ATTACHMENTS}
              multiple
              onChange={handleFileChange}
              disabled={selectedFiles.length >= MAX_ATTACHMENTS}
            />
            {selectedFiles.map((f, i) => (
              <span key={i} className="badge bg-secondary d-inline-flex align-items-center gap-1">
                {f.name}
                <button type="button" className="btn-close btn-close-white" style={{ fontSize: '0.6rem' }} aria-label="Remove" onClick={() => removeFile(i)} />
              </span>
            ))}
          </div>
        </div>
      </div>
    </form>
  );
}

export default HolidayRequestForm;
